import type { FeatureContext } from './featureContext';
import type { EntityId } from '../sim/entity';
import { initDeathScreen } from '../deathScreen';
import {
  initialDeathOverlay,
  applyDeath,
  applyResurrect,
  advanceDeathOverlay,
  deathOverlayView,
} from '../deathOverlay';
import { resurrectCue } from '../audio/combatCue';
import { duelSoundVolume } from './cueVolume';

// Loudness ceiling for the shared resurrection chime at distance 0 (Duel slice D,
// ADR-0131), proximity-scaled for onlookers and full for a participant's own fight.
const DUEL_RESURRECT_VOLUME = 0.7;

/** Injectable seams for unit tests; defaults are the real modules. */
export interface DeathInstallDeps {
  initDeathScreen?: typeof initDeathScreen;
}

/**
 * Death/resurrection presentation, shared by every damage source (duels, storms,
 * King's Justice - the ADR-0150 seam's client-side mirror):
 *
 * - **Resurrection detection** (Duel slice D, ADR-0131): an entity whose replicated
 *   health jumps from 0 (dead) back to full has been resurrected - lift its held
 *   death pose and sound the shared resurrection chime (proximity/participant-
 *   scaled). Derived from the snapshot like footsteps; regen pauses while downed,
 *   so a 0 -> full jump can only be a res.
 * - **The local player's own death cinematic** (Duel slice E, ADR-0136): the black
 *   veil + "You are dead" line + the canvas grayscale filter, driven each frame from
 *   the pure `deathOverlay` state machine (advanced by the local player's replicated
 *   health: 0 = died, a jump back to full = resurrected). Only the dying player sees
 *   this; everyone else watches their grayscale death shroud (the server body swap).
 */
export function installDeath(ctx: FeatureContext, deps: DeathInstallDeps = {}): void {
  const deathScreen = (deps.initDeathScreen ?? initDeathScreen)({ canvas: ctx.canvas });
  let deathOverlay = initialDeathOverlay();
  // Whether the local player is currently dead (health at 0), latched off the
  // snapshot stream so the black cut fires once on death and clears once on res.
  let localDead = false;
  // Per-entity last-seen health, to detect a resurrection (a 0 -> full jump) off
  // the snapshot stream and fire the resurrect cue + lift the held death pose.
  const lastHealth = new Map<EntityId, number>();

  ctx.events.on('snapshot', (world) => {
    const playerId = ctx.playerId();
    const liveHealthIds = new Set<EntityId>();
    for (const entity of world.all()) {
      const health = entity.health;
      if (!health) continue;
      liveHealthIds.add(entity.id);
      const prev = lastHealth.get(entity.id);
      if (prev !== undefined && prev <= 0 && health.current >= health.max) {
        ctx.renderer()?.clearDeath(entity.id);
        const chime = resurrectCue(duelSoundVolume(world, playerId, entity.id, DUEL_RESURRECT_VOLUME));
        if (chime) ctx.audio.play(chime);
      }
      lastHealth.set(entity.id, health.current);
    }
    for (const id of lastHealth.keys()) if (!liveHealthIds.has(id)) lastHealth.delete(id);

    // The local player's own death cinematic: health reaching 0 cuts to black +
    // "You are dead"; the resurrection jump back to full - the same moment the
    // chime above fires - clears it. Latched via `localDead` so the hard cut fires
    // exactly once and the overlay lifts exactly at res. The DOM/grayscale render
    // runs each frame below.
    if (playerId !== undefined) {
      const myHealth = world.get(playerId)?.health;
      if (myHealth) {
        if (!localDead && myHealth.current <= 0) {
          localDead = true;
          deathOverlay = applyDeath(deathOverlay);
        } else if (localDead && myHealth.current >= myHealth.max) {
          localDead = false;
          deathOverlay = applyResurrect(deathOverlay);
        }
      }
    }
  });

  // Advance and render the cinematic each frame: the pure state machine ticks on
  // the frame clock (black hold -> fade -> desaturated ghost hold), and the DOM
  // binder applies this frame's veil / message / grayscale. A no-op look while
  // alive; the death/res signals are set off the snapshot stream above.
  ctx.onFrame((deltaMS) => {
    deathOverlay = advanceDeathOverlay(deathOverlay, deltaMS);
    deathScreen.render(deathOverlayView(deathOverlay));
  }, () => deathOverlay.phase === 'black' || deathOverlay.phase === 'fading');
}
